import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Screen } from "../components/Screen";
import { Ref } from "../components/Ref";
import { Tag } from "../components/Tag";
import { Reveal } from "../components/Reveal";
import { Segmented } from "../components/Segmented";
import { Placeholder } from "../components/Placeholder";
import { MoaDiagram } from "../illustrations/MoaDiagram";
import { useSnak } from "../snak/SnakContext";
const STEPS = [{
  title: "CCR4 on malignant T cells",
  body: "C-C chemokine receptor 4 (CCR4) is expressed on malignant T cells in MF and SS and contributes to their homing to the skin. It is also expressed on some normal T-cell subsets, including regulatory T cells."
}, {
  title: "Mogamulizumab binds CCR4",
  body: "Mogamulizumab is a humanised IgG1 monoclonal antibody directed against CCR4."
}, {
  title: "Antibody-dependent cellular cytotoxicity",
  body: "Its Fc region is defucosylated, which increases binding to Fcγ receptor IIIa on effector cells such as NK cells and enhances antibody-dependent cellular cytotoxicity (ADCC) against CCR4-expressing cells."
}];
export function P6Treatment() {
  const {
    explore
  } = useSnak();
  const [view, setView] = useState<"moa" | "evidence">("moa");
  const [step, setStep] = useState(0);
  const [touched, setTouched] = useState(false);

  // Until the HCP takes over, walk through the mechanism once; any tap hands control back.
  useEffect(() => {
    if (touched || view !== "moa" || step >= STEPS.length - 1) return;
    const t = setTimeout(() => setStep(s => s + 1), step === 0 ? 3200 : 2800);
    return () => clearTimeout(t);
  }, [touched, view, step]);
  return <Screen overline="The treatment" refTarget={4} footnote={<p data-id="element-532" data-mp="7gjwxao7bsuj">
          Indication per EU SmPC; consult the locally applicable prescribing information.<Ref n={4} data-id="element-533" data-mp="cdtmqrfi66iw" />{" "}
          <Placeholder data-id="element-534" data-mp="cf4wr8zjkaig">CH/AT wording if different</Placeholder> Mechanism shown schematically. No superiority over other
          treatments is claimed.
        </p>} data-id="element-531" data-mp="26s48gr0swkkg">
      <Reveal delay={0.1} data-id="element-535" data-mp="dddqjp7x8l4">
        <h1 className="mt-5 font-display text-[38px] font-medium leading-[1.05] tracking-[-0.01em]" data-id="element-536" data-mp="e4zz3wxdtyq">
          Poteligeo<sup className="text-[0.42em]" data-id="element-537" data-mp="ea0deu8k2cuq">®</sup>
          <span className="block italic text-[var(--s-accent)]" data-id="element-538" data-mp="9cqp5hwwk78h">(mogamulizumab).</span>
        </h1>
      </Reveal>
      <Reveal delay={0.2} data-id="element-539" data-mp="nbq603e6bhu3">
        <div className="mt-5 border-l-2 border-[var(--s-accent)] pl-4" data-id="element-540" data-mp="naylbnm474il">
          <p className="overline text-[10px] text-[var(--s-accent)]" data-id="element-541" data-mp="jaelude2jota">Approved indication (EU)</p>
          <p className="mt-1.5 font-display text-[19px] leading-snug" data-id="element-542" data-mp="x1jgt26hrsxa">
            Adults with mycosis fungoides (MF) or Sézary syndrome (SS) who have received at least one prior systemic
            therapy.<Ref n={4} data-id="element-543" data-mp="171trog520o9a" />
          </p>
        </div>
      </Reveal>
      <Reveal delay={0.3} className="mt-7" data-id="element-544" data-mp="121xmweoj8sso">
        <Segmented options={[{
        value: "moa",
        label: "Mechanism of action"
      }, {
        value: "evidence",
        label: "Clinical evidence"
      }]} value={view} onChange={v => {
        setView(v as "moa" | "evidence");
        setTouched(true);
        explore();
      }} data-id="element-545" data-mp="122z2mwghu3gq" />

        <AnimatePresence mode="popLayout" initial={false} data-id="element-546" data-mp="1go4gsta8ffeh">
          {view === "moa" ? <motion.div key="moa" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.25
        }} className="mt-5" data-id="element-547" data-mp="11zi46zbzp1qy">
              <div className="-mx-2" data-id="element-548" data-mp="t03g26jap39m">
                <MoaDiagram step={step} data-id="element-549" data-mp="swz42mzl938e" />
              </div>
              <ol className="mt-2 grid grid-cols-3 gap-2" data-id="element-550" data-mp="xyems8eamxnm">
                {STEPS.map((s, i) => {
              const on = i === step;
              return <li key={s.title} data-id="element-551" data-mp="nzyui23j5icj" data-mpi={i}>
                      <button onClick={() => {
                  setStep(i);
                  setTouched(true);
                  explore();
                }} className={`flex h-full w-full flex-col items-start gap-1 border-t-2 pt-2 text-left text-[12px] leading-snug transition-[border-color,opacity] duration-200 ${on ? "border-[var(--s-accent)] opacity-100" : "border-[var(--s-rule)] opacity-60 hover:opacity-100"}`} data-id="element-552" data-mp="nyxhhs2yels6">
                        <span className="font-display text-[22px] leading-none text-[var(--s-accent)]" data-id="element-553" data-mp="13k8z1jb5z0nb">{i + 1}</span>
                        <span className={on ? "font-semibold" : ""} data-id="element-554" data-mp="ym7fyydqn1bu">{s.title}</span>
                      </button>
                    </li>;
            })}
              </ol>
              <div className="mt-3 min-h-[84px]" data-id="element-555" data-mp="2szgig67v9mkl">
                <AnimatePresence mode="popLayout" initial={false} data-id="element-556" data-mp="2o159dxpebg3f">
                  <motion.p key={step} initial={{
                opacity: 0,
                y: 4
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.25,
                ease: [0.23, 1, 0.32, 1]
              }} className="text-[15px] leading-relaxed" data-id="element-557" data-mp="2nysp3dw60tn7">
                    {STEPS[step].body}
                    <Ref n={4} data-id="element-558" data-mp="3abdo8t3fxn7u" />
                  </motion.p>
                </AnimatePresence>
              </div>
            </motion.div> : <motion.div key="evidence" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.25
        }} className="mt-5" data-id="element-559" data-mp="2hlojc4nm130y">
              <EvidenceTable data-id="element-560" data-mp="1thi3vbvckml" />
            </motion.div>}
        </AnimatePresence>
      </Reveal>
    </Screen>;
}
function EvidenceTable() {
  const rows = [{
    tag: <Tag kind="trial" label="MAVORIC" data-id="element-561" data-mp="6uoobpyd5br5" />,
    design: "Phase 3, randomised, open-label, vs vorinostat",
    population: "372 adults with previously treated MF or SS",
    role: "Pivotal randomised evidence",
    ref: 2
  }, {
    tag: <Tag kind="evidence" label="MINT" data-id="element-562" data-mp="2bwon4l097862" />,
    design: "Real-world, observational, non-randomised",
    population: "109 patients, 18 German centers",
    role: "Outcomes in routine German care (provisional)",
    ref: 1
  }, {
    tag: <Tag kind="guidance" label="Expert recommendations" data-id="element-563" data-mp="1k34o1kmqfe3y" />,
    design: "Expert guidance",
    population: "Germany",
    role: "Clinical positioning",
    ref: 3
  }];
  return <div data-id="element-564" data-mp="1syu73n0pjwe6">
      <div className="divide-y divide-[var(--s-rule)] border-y border-[var(--s-rule)]" data-id="element-565" data-mp="1szdn2y6ssapk">
        {rows.map(r => <div key={r.ref} className="py-4" data-id="element-566" data-mp="10h88d886gqa4">
            <span data-id="element-567" data-mp="10fx65cudrql4">
              {r.tag}
              <Ref n={r.ref} data-id="element-568" data-mp="10evnonb1c1td" />
            </span>
            <p className="mt-1.5 font-display text-[18px] leading-snug" data-id="element-569" data-mp="welcmw8dyl7n">{r.design}</p>
            <p className="mt-1 text-[13px] text-[var(--s-muted)]" data-id="element-570" data-mp="mbg1q6038hwj">
              {r.population} · {r.role}
            </p>
          </div>)}
      </div>
      <p className="mt-3 text-[11.5px] leading-relaxed text-[var(--s-muted)]" data-id="element-571" data-mp="r49b576i4eey">
        Designs differ; results from these sources should not be compared directly. No superiority over other treatments
        is claimed.
      </p>
    </div>;
}